import { cn } from "@/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { ChevronsUpDown } from "lucide-react";
import React, { forwardRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Skeleton } from "./ui/skeleton";

const getInitials = (name?: string): string => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase();
};

export const UserProfileButton = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>((props, ref) => {
    const { user, isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return (
      <div
        ref={ref}
        className="-mx-2 flex flex-1 items-center gap-2 rounded-md p-2"
        {...props}
      >
        <div>
          <Skeleton className="size-10 h-10 w-10 rounded-full bg-border" />
        </div>
        <div className="ml-2 w-full space-y-1">
          <Skeleton className="h-2 w-2/3 bg-border" />
          <Skeleton className="h-2 w-3/5 bg-border" />
        </div>
      </div>
    );
  }

  if(!isAuthenticated) {
    return (
      <div
        ref={ref}
        className="-mx-2 flex flex-1 items-center gap-2 rounded-md p-2"
        {...props}
      >
    Not logged in
      </div>
    )
  }
  return (
    <div
      ref={ref}
      className={cn(
        "-mx-2 flex flex-1 items-center gap-2 rounded-md border border-transparent p-2 transition-all hover:cursor-pointer hover:border-border hover:bg-background hover:shadow-sm",
        "data-[state=open]:border-border data-[state=open]:bg-background data-[state=open]:shadow-sm",
      )}
      {...props}
    >
      <div className="relative">
        <Avatar>
          {user?.picture && <AvatarImage src={user.picture} />}
          <AvatarFallback className="bg-border text-muted">
            {getInitials(user?.name)}
          </AvatarFallback>
        </Avatar>
      </div>
      <p className="flex min-w-0 flex-1 flex-col">
        <span className="block truncate text-left text-sm font-semibold text-muted-foreground">
          {user?.name}
        </span>
        <span className="block truncate text-left text-xs font-normal text-muted-foreground ">
          {user?.email}
        </span>
      </p>
      <ChevronsUpDown className="size-4 text-muted-foreground" />
    </div>
  );
});

UserProfileButton.displayName = "UserProfileButton";
